// Copyright (c) Bentley Systems
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { getTotalHeightWithMargins } from "./helper-functions";

/**
 * Hook that tracks the total height (including margins) of one or more element refs,
 * updating on window resize.
 *
 * @param refs - An array of React refs whose heights should be measured.
 * @returns An array of heights in the same order as the provided refs.
 *
 * @example
 * const headerRef = useRef<HTMLDivElement>(null);
 * const buttonsRef = useRef<HTMLDivElement>(null);
 * const [headerHeight, buttonsHeight] = useElementHeights(headerRef, buttonsRef);
 */
export const useElementHeights = (
  ...refs: RefObject<HTMLElement | null>[]
): number[] => {
  const [heights, setHeights] = useState<number[]>(() => refs.map(() => 0));

  // Store refs in a mutable ref to avoid the rest-parameter array identity
  // from changing on every render, which would cause an infinite loop.
  const refsRef = useRef(refs);
  refsRef.current = refs;

  const calculateHeights = useCallback(() => {
    setHeights(
      refsRef.current.map((ref) => getTotalHeightWithMargins(ref.current)),
    );
  }, []);

  useEffect(() => {
    calculateHeights();
    window.addEventListener("resize", calculateHeights);
    return () => {
      window.removeEventListener("resize", calculateHeights);
    };
  }, [calculateHeights]);

  return heights;
};
